import { Building2, Loader2 } from 'lucide-react'
import { bookableSpaces } from '@lib/helpers/event-track'
import type { VenueDto, VenueSpaceDto } from '@lib/dto/venue.dto'
import type { EventTrackDto } from '@lib/dto/event-track.dto'
import type { TrackAvailabilityDto } from '@lib/dto/track-availability.dto'
import { objectPositionOf, type FocalPoint } from '@lib/helpers/focal-point'
import { Badge } from '@/components/ui/badge'
import { ChoiceCard } from '@/components/ui/choice-card'
import { cn } from '@/lib/utils'

/**
 * Choosing which lanes an event books, as cards rather than a dropdown.
 *
 * A track is a *part of the venue* — a set of rooms — and the name alone
 * ("Track 1", "Track 2") does not say which part. A dropdown asked people to
 * remember what those names meant; a card shows the room, lists what is in it,
 * and says whether it is free.
 *
 * **Several lanes at once**, because an event may take the whole venue. The API
 * refused a set until the conflict rule could police one; it can, so this is
 * checkboxes rather than radios.
 *
 * **A taken lane is not disabled.** The write is what decides, and it decides
 * again at the moment of writing under a lock — so a card that refused to be
 * clicked would be enforcing a stale answer, and would also make a *canceled*
 * booking's lane unpickable while the refusal it is predicting would not
 * happen. It is marked, named, and left clickable; the 409 is the authority.
 */

/** The photograph that stands for a track: its first room's first picture. */
export function trackCover(spaces: readonly VenueSpaceDto[]): {
  src: string | null
  focalPoint: FocalPoint | null
} {
  // Sorted rather than trusting the order the API happened to return: the card
  // shows one room out of several, so *which* one must not change between loads.
  const first = [...spaces].sort((a, b) => a.sortOrder - b.sortOrder)[0]
  const photo = first ? [...first.photos].sort((a, b) => a.sortOrder - b.sortOrder)[0] : undefined
  return { src: photo?.url ?? null, focalPoint: photo?.focalPoint ?? null }
}

interface TrackPickerProps {
  venue: VenueDto
  /** The chosen track ids. Empty means nothing chosen yet. */
  value: readonly string[]
  onChange: (trackIds: string[]) => void
  /** One entry per lane, or undefined while the answer is still in flight. */
  availability?: readonly TrackAvailabilityDto[] | undefined
  /** True while availability is loading, so a lane can say so instead of lying. */
  loading?: boolean
  /**
   * True when the answer could not be fetched.
   *
   * Distinguished from "nothing asked", because they were indistinguishable and
   * the wrong one is actively misleading: a refused window (16:00 to 16:00 is
   * not a booking) made every card say "Pick a date to check" with a date
   * plainly in the field.
   */
  failed?: boolean
  /** Groups the checkboxes. Distinct per form, not per venue. */
  name?: string
}

export function TrackPicker({
  venue,
  value,
  onChange,
  availability,
  loading = false,
  failed = false,
  name = 'event-track',
}: TrackPickerProps) {
  const tracks = [...venue.tracks].sort((a, b) => a.sortOrder - b.sortOrder)
  const answerFor = (trackId: string): TrackAvailabilityDto | undefined =>
    availability?.find((a) => a.trackId === trackId)

  function toggle(trackId: string): void {
    onChange(value.includes(trackId) ? value.filter((id) => id !== trackId) : [...value, trackId])
  }

  return (
    // A named group, which the radio version had and this lost: without it a
    // screen reader reads six checkboxes with no sense that they are one
    // choice. `Field`'s label is not associated with this div, so it says the
    // name itself.
    <div role="group" aria-label="Track" className="grid gap-2 sm:grid-cols-2">
      {tracks.map((track) => (
        <TrackCard
          key={track.id}
          track={track}
          venue={venue}
          name={name}
          selected={value.includes(track.id)}
          answer={answerFor(track.id)}
          loading={loading}
          failed={failed}
          onToggle={() => {
            toggle(track.id)
          }}
        />
      ))}
    </div>
  )
}

/**
 * What a lane's card says about itself, under the rooms.
 *
 * A `Badge`, which is what the app already uses for status — and status is
 * exactly what this is. Color is never the only carrier: each tone has its own
 * words, so the three states are distinguishable without it.
 */
function AvailabilityBadge({
  answer,
  loading,
  failed,
}: {
  answer: TrackAvailabilityDto | undefined
  loading: boolean
  failed: boolean
}) {
  if (loading) {
    return (
      <Badge tone="neutral">
        <Loader2 className="h-3 w-3 animate-spin" /> Checking…
      </Badge>
    )
  }
  // A failure is not "nothing asked". Saying "pick a date" with a date in the
  // field sends somebody looking for a mistake they did not make.
  if (failed) {
    return <Badge tone="danger">Could not check</Badge>
  }
  // No answer yet, or an answer of `unknown`: both mean there is nothing to
  // report. An undated event holds no lane, so "free" would be a claim nobody
  // made — see `trackAvailabilityStatusSchema`.
  if (answer === undefined || answer.status === 'unknown') {
    return <Badge tone="neutral">Pick a date to check</Badge>
  }
  if (answer.status === 'free') {
    return (
      <Badge tone="success" dot>
        Free
      </Badge>
    )
  }
  // `takenBy` is guaranteed here: narrowing on the status is what gives it.
  // This used to be `answer.takenBy ? … : ''` — a branch for a state the server
  // never sends, which would have rendered "Taken" with no name if it did.
  return (
    <Badge tone="warning" dot>
      Taken — {answer.takenBy.eventName}
    </Badge>
  )
}

function TrackCard({
  track,
  venue,
  name,
  selected,
  answer,
  loading,
  failed,
  onToggle,
}: {
  track: EventTrackDto
  venue: VenueDto
  name: string
  selected: boolean
  answer: TrackAvailabilityDto | undefined
  loading: boolean
  failed: boolean
  onToggle: () => void
}) {
  // `bookableSpaces`, not `spaceIds`: a space nobody assigned to a track stays
  // venue-wide and *is* bookable here, so listing only the track's own rooms
  // would understate what this choice gets you. One implementation, shared with
  // the editor and the API — see its comment for the bug that came of having
  // three.
  const spaces = bookableSpaces(venue.spaces, [track.id])
  const cover = trackCover(spaces)
  // Not while a fresh answer is in flight: the window is part of the query key,
  // so `answer` is the *previous* window's until the new one lands — and this
  // strip states a consequence, which must not be stated about a stale window.
  const taken = !loading && !failed && answer?.status === 'taken'

  return (
    <ChoiceCard
      type="checkbox"
      name={name}
      value={track.id}
      checked={selected}
      onCheckedChange={onToggle}
      media={<TrackThumb src={cover.src} focalPoint={cover.focalPoint} />}
      title={<span className="block truncate">{track.name}</span>}
      description={
        spaces.length === 0 ? (
          // Worth saying plainly: booking this track gets no rooms to plan in,
          // which is a venue misconfiguration and not something to discover
          // after the event exists.
          'No rooms yet'
        ) : (
          <SpaceNames spaces={spaces} />
        )
      }
    >
      <span className="block pt-1.5">
        <AvailabilityBadge answer={answer} loading={loading} failed={failed} />
      </span>
      {/* A picked lane that is taken is the one combination worth shouting
          about, because the create will be refused. Said here rather than
          only in the badge above, which reads as a status and not a problem. */}
      {selected && taken ? (
        <span className="mt-1 block text-xs text-danger-ink">
          This lane is taken for that time — the booking will be refused.
        </span>
      ) : null}
    </ChoiceCard>
  )
}

/**
 * The lane's first room, as a framed 4:3 thumbnail — or, with no photograph,
 * the empty well on the plan grid. Small, because a photograph is evidence
 * beside the choice, not the choice: the old 16:10 art made two tracks fill
 * the first screen of a form.
 */
function TrackThumb({ src, focalPoint }: { src: string | null; focalPoint: FocalPoint | null }) {
  return (
    <span
      aria-hidden
      className={cn(
        'relative grid h-[54px] w-[72px] shrink-0 place-items-center overflow-hidden rounded-sm bg-surface-sunken text-ink-subtle',
        'after:pointer-events-none after:absolute after:inset-0 after:rounded-sm after:shadow-[var(--ds-media-ring)]',
        src === null && 'plan-grid-fine',
      )}
    >
      {src !== null ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: objectPositionOf(focalPoint, UNFOCUSED_POSITION) }}
        />
      ) : (
        <Building2 className="h-4 w-4" />
      )}
    </span>
  )
}

/** A doorway sits above center in most venue photographs, so an unfocused crop keeps the upper part. */
const UNFOCUSED_POSITION = '50% 40%'

/** The rooms, named — all of them up to a point, then a count. */
function SpaceNames({ spaces }: { spaces: readonly VenueSpaceDto[] }) {
  const sorted = [...spaces].sort((a, b) => a.sortOrder - b.sortOrder)
  const shown = sorted.slice(0, 3)
  const rest = sorted.length - shown.length
  return (
    <>
      {shown.map((s) => s.name).join(', ')}
      {rest > 0 ? ` +${String(rest)} more` : ''}
    </>
  )
}
